import React from 'react';
import { Card, List, Empty, Typography, Space, Tag, Button } from 'antd';
import { SoundOutlined, PlayCircleOutlined } from '@ant-design/icons';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import type { AudioData, TranscriptData } from '../../types/dashboard';

const { Text } = Typography;

interface AudioSectionProps {
  audioData: AudioData[];
  transcriptData: TranscriptData[];
  audioLoading: Record<string, boolean>;
  debugData?: any;
  isRealtime?: boolean;
  onLoadAudio: (audioId: string) => void;
  onRefreshAudio?: () => void;
}

const AudioSection: React.FC<AudioSectionProps> = ({ 
  audioData, 
  transcriptData, 
  audioLoading, 
  debugData, 
  isRealtime = false,
  onLoadAudio,
  onRefreshAudio
}) => {
  return (
    <Card 
      title={<><SoundOutlined /> 音声履歴 ({audioData.length}件)</>}
      extra={isRealtime && onRefreshAudio && <Button size="small" onClick={onRefreshAudio}>音声更新</Button>}
      style={{ marginBottom: 16 }}
    >
      <details style={{ marginBottom: 8, fontSize: '12px' }}>
        <summary>🔍 音声 API応答</summary>
        <pre style={{ background: '#f5f5f5', padding: 8, fontSize: '10px', overflow: 'auto', maxHeight: 200 }}>
          {JSON.stringify(debugData?.audio, null, 2)}
        </pre>
      </details>
      <details style={{ marginBottom: 8, fontSize: '12px' }}>
        <summary>🔍 転写 API応答</summary>
        <pre style={{ background: '#f5f5f5', padding: 8, fontSize: '10px', overflow: 'auto', maxHeight: 200 }}>
          {JSON.stringify(debugData?.transcripts, null, 2)}
        </pre>
      </details>
      {audioData.length > 0 ? (
        <List
          dataSource={audioData}
          size="small"
          renderItem={(item) => {
            const transcript = transcriptData.find(t => t.audio_id === item.id);
            const truncatedText = transcript ? 
              (transcript.transcript_text.length > 50 ? 
                transcript.transcript_text.substring(0, 50) + '...' : 
                transcript.transcript_text) : 
              '転写中...';
            return (
              <List.Item style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
                  <PlayCircleOutlined style={{ color: transcript ? '#52c41a' : '#faad14', marginRight: 8, marginTop: 4 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Space>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>
                          {truncatedText}
                        </span>
                        {!isRealtime && (
                          <Tag color={
                            item.status === 'transcribed' ? 'green' :
                            item.status === 'transcribing' ? 'blue' :
                            item.status === 'stored' ? 'orange' :
                            item.status === 'failed' ? 'red' : 'default'
                          }>
                            {item.status === 'transcribed' ? '完了' :
                             item.status === 'transcribing' ? '処理中' :
                             item.status === 'stored' ? '待機中' :
                             item.status === 'failed' ? '失敗' : item.status || '不明'}
                          </Tag>
                        )}
                        {isRealtime && item.event_id && (
                          <Tag color="green" style={{ fontSize: '10px' }}>NEW</Tag>
                        )}
                      </Space>
                    </div>
                    {transcript ? (
                      <div>
                        <div style={{ marginBottom: 8, padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: 4 }}>
                          <Text>{transcript.transcript_text}</Text>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          信頼度: {Math.round(transcript.confidence * 100)}% | {item.duration_ms ? `${Math.round(item.duration_ms / 1000)}秒` : '不明'} | {new Date(item.start_time.replace(/Z$/, '')).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {new Date(item.start_time.replace(/Z$/, '')).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ width: '100%' }}>
                  {item.s3_url ? (
                    <AudioPlayer
                      src={item.s3_url}
                      showJumpControls={false}
                      showDownloadProgress={false}
                      layout="horizontal-reverse"
                      style={{ fontSize: '12px' }}
                    />
                  ) : !isRealtime ? (
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <Button 
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        loading={audioLoading[item.id]}
                        onClick={() => onLoadAudio(item.id)}
                      >
                        音声を読み込み
                      </Button>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <Button 
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        loading={audioLoading[item.id]}
                        onClick={() => onLoadAudio(item.id)}
                      >
                        音声を読み込み
                      </Button>
                    </div>
                  )}
                </div>
              </List.Item>
            );
          }}
          locale={{ emptyText: '音声データがありません' }}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="音声データがありません" />
      )}
    </Card>
  );
};

export default AudioSection;